import * as React from 'react'
import { vsprintf } from 'sprintf-js'

import { i18n, useLogger } from '@thg-commerce/enterprise-core'

import { TopNProducts } from '@thg-commerce/enterprise-widget-topn'
import { TopProductProps } from '@thg-commerce/enterprise-widget-topn/src/TopNProducts'

import { ArticleContext } from '../../components'

import { TopNWidgetItem } from '../../../post-content'

export interface TopNWidgetRendererProps {
  isAmp: boolean
  items: [TopNWidgetItem, ...TopNWidgetItem[]]
}

export const TopNWidgetRenderer = (props: TopNWidgetRendererProps) => {
  const logger = useLogger()

  if (!props.items) {
    logger.error(`TopN Renderer was invoked with no widget data.`)
    return null
  }

  const ArticleCtx = React.useContext(ArticleContext)

  const buyAriaLabel = i18n('general.productbuynow.label')
  const buyText = i18n('general.productbuynow.text')
  const quickBuyText = i18n('general.product.quickbuy.text')
  const fromText = i18n('product.price.from.text')
  const reviewsStarsLabel = i18n('reviews.stars.label')
  const reviewsLabel = i18n('reviews.reviews.label')

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${reviewsStarsLabel} 
    ${total} ${reviewsLabel}`

  const productListData: TopProductProps[] = []
  props.items.forEach((item) => {
    const product = ArticleCtx.products.get(item.sku)

    if (product) {
      const price = product.price || { displayValue: '' }
      const title = item.title || product.title

      let productDescription = item.description
      if (!productDescription) {
        const richValueContent = product?.content?.find(
          ({ key }) => key === 'synopsis',
        )?.value?.value?.[0]

        productDescription = (richValueContent?.content?.[0]?.content ??
          '') as string
      }

      productListData.push({
        sku: product.sku,
        title: {
          value: title,
        },
        url: item.link || product.url,
        image: product.images?.[0].largeProduct && {
          urls: {
            largeProduct: product.images[0].largeProduct,
          },
          lazy: true,
        },
        reviews: product.reviews
          ? {
              starRating: product.reviews?.averageScore ?? 0,
              numberOfReviews: product.reviews?.total,
              screenReaderOnlyText: screenReaderReviewText({
                score: product.reviews?.averageScore ?? 0,
                total: product.reviews?.total,
              }),
            }
          : undefined,
        price: product.price
          ? {
              fromText,
              price: {
                defaultPrice: price.displayValue,
              },
              hasFromPrice: product?.hasFromPrice,
            }
          : undefined,
        button: {
          title: buyText,
          ariaLabel: vsprintf(buyAriaLabel, [title]),
          quickBuyTitle: quickBuyText,
        },
        description: productDescription,
      })
    }
  })
  return <TopNProducts isAmp={props.isAmp} products={productListData} />
}
